/**
 * triageDecisionChange.js
 * -----------------------
 * A clinician changing their own triage decision before the letter is signed
 * off (TDI-695; Tom King, R-025 option B). Mirrors reopen_refusal in
 * python-functions/rms_functions/rms_triage_reopen.py, which is the rule the
 * server enforces; keep the two in step.
 *
 * Reopening moves the clinician's review into `supersededReviews` on the same
 * pathway (never deleted). These helpers read that list to put the decision
 * screens back in front of the clinician with their own answers.
 */

import {
  pathwayReviews,
  pathwayTriage,
  pathwayTriageDocuments,
  PATHWAY,
} from './triagePathways';
import { ADHD_SIGNPOSTING_OPTIONS } from '../constants/adhdSignposting';
import { AUTISM_SIGNPOSTING_OPTIONS } from '../constants/autismSignposting';

export const CHANGE_DECISION_LABEL = 'Change my decision';
export const CHANGE_DECISION_TITLE = 'Change your decision?';
export const CHANGE_DECISION_BODY =
  "Your letter draft will be discarded and written again from your new decision. You'll need to check and sign it off again.";
export const CHANGE_DECISION_FIRST_REJECTION_BODY =
  "You can change your answers until a second reviewer picks this up. If you now accept the referral, it won't go to a second review.";
export const KEEP_DECISION_LABEL = 'Keep my decision';
// For the deciding clinician before sign-off; everyone else keeps
// "... can no longer be changed."
export const DECISION_CHANGEABLE_NOTICE =
  'This triage decision has been recorded. You can change it until the letter is signed off.';
// An autism first rejection has no letter: it can be changed until a second
// reviewer picks it up. Not ticket wording; for Jasmine.
export const FIRST_REJECTION_CHANGEABLE_NOTICE =
  'This triage decision has been recorded. You can change your answers until a second reviewer picks this up.';

const reviewerId = (review) => review?.decidedBy?.clinicianId || null;

/**
 * Whether this clinician may change their decision on this pathway now, and
 * if so which kind of change it is.
 *
 * @returns {{ allowed: boolean, firstRejection: boolean }}
 */
export function triageDecisionChange(patient, pathway, clinicianId) {
  const no = { allowed: false, firstRejection: false };
  if (!patient || !clinicianId) return no;
  const reviews = pathwayReviews(patient, pathway);
  const last = reviews[reviews.length - 1];
  if (!last || reviewerId(last) !== clinicianId) return no;

  const triage = pathwayTriage(patient, pathway) || {};
  const docs = pathwayTriageDocuments(patient, pathway) || {};
  if (triage.status === 'complete') {
    if (docs.signOff || triage.letterSignOff === 'signed_off') return no;
    if (docs.assignedReviewerId && docs.assignedReviewerId !== clinicianId) return no;
    return { allowed: true, firstRejection: false };
  }
  if (
    triage.status === 'pending_second_review'
    && reviews.length === 1
    && last.accepted === false
    && !triage.assignedClinicianId
  ) {
    return { allowed: true, firstRejection: true };
  }
  return no;
}

export function canChangeTriageDecision(patient, pathway, clinicianId) {
  return triageDecisionChange(patient, pathway, clinicianId).allowed;
}

/**
 * The clinician's most recent superseded review on this pathway, while the
 * reopen that superseded it is still open (the pathway is back in review with
 * them and no newer decision has been recorded). Null otherwise.
 */
export function reopenedReviewFor(patient, pathway, clinicianId) {
  if (!patient || !clinicianId) return null;
  const triage = pathwayTriage(patient, pathway) || {};
  const pointer = triage.reopenedFrom;
  if (!pointer || pointer.clinicianId !== clinicianId) return null;
  const superseded = Array.isArray(triage.supersededReviews) ? triage.supersededReviews : [];
  const review = superseded[pointer.supersededIndex];
  return review && reviewerId(review) === clinicianId ? review : null;
}

const trimmed = (value) => (typeof value === 'string' ? value : '');

/**
 * The decision screens' answers rebuilt from a superseded review, in the
 * shape the forms keep as their draft, so the screens reopen with every
 * answer, comment, note and signposting choice the clinician recorded (not
 * blank, and not a platform pre-fill). Tags are live on the referral, so they
 * are not part of this.
 *
 * @param {object} review - the superseded review
 * @param {'adhd'|'asd'} pathway
 * @param {Array} [availableClinicians] - ADHD only: who can be marked involved
 */
export function initialAnswersFromReview(review, pathway, availableClinicians = []) {
  if (!review) return null;
  const options = pathway === PATHWAY.ASD ? AUTISM_SIGNPOSTING_OPTIONS : ADHD_SIGNPOSTING_OPTIONS;
  const knownOptionIds = new Set(options.map((o) => o.optionId));
  const other = review.signpostingOther || null;
  const otherFactors = review.otherFactors || {};
  const risks = otherFactors.risks || {};
  const common = {
    stepIndex: 0,
    evidence: review.evidence || {},
    otherFactorsComment: trimmed(otherFactors.comment),
    riskPresent: typeof risks.presentNeedingReduction === 'boolean' ? risks.presentNeedingReduction : null,
    riskDetail: trimmed(risks.detail),
    conclusion: trimmed(review.conclusion),
    accepted: typeof review.accepted === 'boolean' ? review.accepted : null,
    signpostingSelected: (review.signpostingSelections || [])
      .map((s) => s?.optionId)
      .filter((id) => knownOptionIds.has(id)),
    signpostingOtherOn: Boolean(other?.title),
    signpostingOtherTitle: trimmed(other?.title),
    signpostingOtherText: trimmed(other?.text),
  };

  if (pathway === PATHWAY.ASD) {
    const priority = otherFactors.priorityProblems || {};
    return {
      ...common,
      followThrough: otherFactors.followThrough || {},
      riskInterventions: trimmed(risks.interventions),
      priorityPresent: typeof priority.present === 'boolean' ? priority.present : null,
      priorityPlans: typeof priority.plansInPlace === 'boolean' ? priority.plansInPlace : null,
      priorityDetail: trimmed(priority.detail),
      // The manual consensus tick was removed for go-live (TDI-784, R-053);
      // an outcome disagreement still flags itself server-side when the new
      // decision is recorded.
    };
  }

  const listed = new Set((availableClinicians || []).map((c) => c.clinicianId));
  return {
    ...common,
    involvedIds: (review.cliniciansInvolved || [])
      .filter((c) => c?.involved && c.clinicianId !== reviewerId(review) && listed.has(c.clinicianId))
      .map((c) => c.clinicianId),
  };
}
